import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, Listing } from '../api';

const PAGE_SIZE = 20;

const MyListings: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [editingQuantity, setEditingQuantity] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async (append = false) => {
    if (append) setLoadingMore(true);
    else { setOffset(0); setHasMore(true); }
    try {
      const fetchOffset = append ? offset : 0;
      const data = await listingsAPI.getMine({ limit: PAGE_SIZE, offset: fetchOffset });
      const arr = Array.isArray(data) ? data : [];
      if (append) {
        setListings(prev => [...prev, ...arr]);
      } else {
        setListings(arr);
      }
      setHasMore(arr.length >= PAGE_SIZE);
      setOffset(fetchOffset + arr.length);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load listings');
    } finally {
      setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  const handleQuantityChange = (listingId: number, newQuantity: number) => {
    setEditingQuantity({ ...editingQuantity, [listingId]: newQuantity });
  };

  const updateQuantity = async (listingId: number) => {
    const quantity = editingQuantity[listingId];
    if (quantity === undefined || quantity < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await listingsAPI.update(listingId, { quantity });
      // Remove from editing state
      const newEditing = { ...editingQuantity };
      delete newEditing[listingId];
      setEditingQuantity(newEditing);
      // Reload listings
      loadListings();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update quantity');
    }
  };

  const handleDelete = async (listingId: number, listingTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${listingTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await listingsAPI.delete(listingId);
      // Reload listings
      await loadListings();
    } catch (err: any) {
      let msg = err.response?.data?.error || err.message || 'Failed to delete listing';
      if (typeof msg === 'string' && (msg.includes("couldn't be read") || msg.includes('is missing') || msg.includes('Unexpected token'))) {
        msg = 'Failed to delete listing. Please refresh the page.';
      }
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete listing');
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Free';
    return `$${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading your listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">My Listings</h1>
            <p className="text-sm sm:text-base text-gray-600">Manage your listings and update quantities</p>
          </div>
          <Link
            to="/create"
            className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 sm:px-6 rounded-lg transition text-sm sm:text-base w-full sm:w-auto text-center"
          >
            Create New Listing
          </Link>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {listings.length === 0 ? (
          <div className="rounded-lg shadow-md p-6 sm:p-12 text-center">
            <div className="text-4xl sm:text-6xl mb-4">📝</div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-2">No listings yet</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">Create your first listing to start selling</p>
            <Link
              to="/create"
              className="inline-block bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-6 rounded-lg transition text-sm sm:text-base"
            >
              Create a Listing
            </Link>
          </div>
        ) : (
          <>
          <div className="space-y-3 sm:space-y-4">
            {listings.map((listing) => {
              const isSold = listing.sold === 1 || listing.sold === true;
              const currentQuantity = listing.quantity !== undefined ? listing.quantity : 1;
              const editingQty = editingQuantity[listing.id] !== undefined 
                ? editingQuantity[listing.id] 
                : currentQuantity;

              return (
                <div key={listing.id} className="rounded-lg shadow-md overflow-hidden border border-gray-200">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4">
                      {listing.image_url ? (
                        <img
                          src={listing.image_url}
                          alt={listing.title}
                          className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0 self-center sm:self-start"
                        />
                      ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 self-center sm:self-start">
                          <span className="text-3xl sm:text-4xl">📦</span>
                        </div>
                      )}
                      <div className="flex-1 w-full min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 sm:mb-2 gap-2 sm:gap-0">
                          <div className="flex-1 min-w-0">
                            <Link
                              to={`/listings/${listing.id}`}
                              className="text-lg sm:text-xl font-semibold text-gray-800 hover:text-green-600 transition break-words block"
                            >
                              {listing.title}
                            </Link>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{listing.description}</p>
                          </div>
                          <div className="text-left sm:text-right flex-shrink-0">
                            <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatPrice(listing.price)}</p>
                            {isSold && (
                              <span className="inline-block bg-red-500 text-white px-2 py-1 rounded text-xs font-bold mt-1">
                                SOLD
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 gap-3 sm:gap-0">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-6 text-xs sm:text-sm text-gray-600">
                            <span>📍 {listing.city_name}, {listing.state_abbreviation}</span>
                            <span>🏷️ {listing.category}</span>
                            <span>📅 {new Date(listing.created_at).toLocaleDateString()}</span>
                          </div>

                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                            <div className="flex items-center justify-between sm:justify-start space-x-2 sm:space-x-2">
                              <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Quantity:</label>
                              {editingQuantity[listing.id] !== undefined ? (
                                <div className="flex items-center space-x-2 flex-1 sm:flex-initial">
                                  <input
                                    type="number"
                                    min="0"
                                    value={editingQty}
                                    onChange={(e) => handleQuantityChange(listing.id, parseInt(e.target.value) || 0)}
                                    className="w-16 sm:w-20 px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm"
                                  />
                                  <button
                                    onClick={() => updateQuantity(listing.id)}
                                    className="px-2 sm:px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs sm:text-sm font-medium"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newEditing = { ...editingQuantity };
                                      delete newEditing[listing.id];
                                      setEditingQuantity(newEditing);
                                    }}
                                    className="px-2 sm:px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs sm:text-sm font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <span className={`font-semibold text-sm sm:text-base ${currentQuantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {currentQuantity}
                                  </span>
                                  <button
                                    onClick={() => handleQuantityChange(listing.id, currentQuantity)}
                                    className="px-2 sm:px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs sm:text-sm font-medium"
                                  >
                                    Edit Qty
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => navigate(`/create/${listing.id}`)}
                                className="flex-1 sm:flex-initial px-3 py-1.5 sm:py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs sm:text-sm font-medium"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDelete(listing.id, listing.title)}
                                className="flex-1 sm:flex-initial px-3 py-1.5 sm:py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs sm:text-sm font-medium"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => loadListings(true)}
                disabled={loadingMore}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:opacity-70 text-white font-medium rounded-lg transition flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyListings;

